import {
	type DateKey,
	firstPlayed,
	flowRecords,
	formatDuration,
	type LevelRecord,
	levelRecord,
	monthView,
	shiftMonth,
	solveHistory,
	streakStatus,
	summarizeStats,
} from '@arrow/game-engine';
import { type Level, LEVELS } from '@arrow/puzzle-schema';
import { useEffect, useMemo, useRef, useState } from 'react';

import { type ChallengeResults, fetchChallengeResults, fetchPercentile } from '../api/client.ts';
import { setPageTitle } from '../app/page-title.ts';
import { prefs } from '../app/prefs.ts';
import { Link } from '../app/router.tsx';
import { AppMenu } from '../components/AppMenu.tsx';
import { BrandRow } from '../components/BrandRow.tsx';
import { StreakGuard } from '../push/StreakGuard.tsx';
import { Flame, Wedge } from '../components/icons.tsx';
import { useInstallFlow } from '../components/install/Install.tsx';
import { InstallRow, lineCopy } from '../components/install/Surfaces.tsx';
import { Nib } from '../components/Nib.tsx';
import { TabBar } from '../components/TabBar.tsx';
import { useNibTaps } from '../dev/mode.ts';
import { track } from '../platform/analytics.ts';
import { myChallenges } from '../platform/challenges.ts';
import { challengesTaken, tallyTaken } from '../platform/challenges-taken.ts';
import { getDeviceId } from '../platform/identity.ts';
import { lineVisible } from '../pwa/install-state.ts';
import type { Persistence } from '../storage/persistence.ts';
import { CalendarKey, MonthCalendar } from './stats/Calendar.tsx';
import {
	CHALLENGES_ASKED,
	type ChallengeTally,
	defaultLevel,
	helpLine,
	longDate,
	percentileLine,
	plural,
	streakLine,
	tallyChallenges,
	yearOf,
} from './stats/model.ts';
import { ChallengesBody, EveryTime, FlowBody, LevelCards, Section } from './stats/Sections.tsx';
import { TimesChart } from './stats/TimesChart.tsx';
import './stats/stats.css';
import { LEVEL_NAME } from './today-model.ts';
import { Toast } from '../ui/index.ts';

/** "a and b." / "a." from the parts that are there. */
function andList(parts: (string | null)[]): string {
	const xs = parts.filter((x): x is string => !!x);
	return xs.length ? `${xs.join(' and ')}.` : '';
}

/** What Stats asks the api for: injectable for tests. Both never throw past here. */
export interface StatsDeps {
	percentile: (n: number, level: Level, ms: number) => Promise<number | null>;
	challengeResults: (code: string) => Promise<ChallengeResults | null>;
}

const defaultDeps: StatsDeps = {
	percentile: (n, level, ms) => fetchPercentile(n, level, ms).then((s) => s?.percentile ?? null),
	challengeResults: (code) => fetchChallengeResults(code, getDeviceId()).catch(() => null),
};

/**
 * Stats: the streak and the calendar of days played first, then your best time on each level (the cards
 * pick the level for its times chart), Flow and your challenges (sent and taken), and
 * everything (with the help you used) in a line at the foot. All local and anonymous; the api
 * adds only a percentile (when it has enough solvers) and how your challenges went.
 */
export function Stats({
	todayKey,
	persistence,
	deps = defaultDeps,
}: {
	todayKey: DateKey;
	persistence: Persistence;
	deps?: StatsDeps;
}) {
	const stats = useMemo(() => persistence.loadStats(), [persistence]);
	const summary = useMemo(() => summarizeStats(stats, todayKey), [stats, todayKey]);
	const streak = useMemo(() => streakStatus(stats, todayKey), [stats, todayKey]);
	const records = useMemo(() => Object.fromEntries(LEVELS.map((l) => [l, levelRecord(stats, l)])) as Record<Level, LevelRecord>, [stats]);
	const flow = useMemo(() => flowRecords(stats), [stats]);
	const thisYear = yearOf(todayKey);

	const [level, setLevel] = useState<Level>(() => defaultLevel(records, prefs().lastLevel()));
	const solves = useMemo(() => solveHistory(stats, level), [stats, level]);
	const record = records[level];

	// The calendar pages from the first month you played to this one.
	const now = { year: thisYear, month: Number(todayKey.slice(5, 7)) };
	const first = firstPlayed(stats);
	const firstYm = first ? { year: yearOf(first), month: Number(first.slice(5, 7)) } : now;
	const [ym, setYm] = useState(now);
	const month = useMemo(() => monthView(stats, ym.year, ym.month, todayKey), [stats, ym.year, ym.month, todayKey]);
	const ymIndex = (x: { year: number; month: number }) => x.year * 12 + x.month;

	const viewed = useRef(false);
	useEffect(() => {
		setPageTitle('You');
		if (viewed.current) return;
		viewed.current = true;
		track('stats_viewed', {});
	}, []);

	// Today's percentile at the picked level: only for a clean solve today, only if the api has one.
	const latest = record.latest;
	const askPct = latest && latest.dateKey === todayKey && latest.revealsUsed === 0 ? latest : null;
	const [pct, setPct] = useState<{ key: string; value: number | null } | null>(null);
	const pctKey = askPct ? `${askPct.number}:${level}:${askPct.elapsedMs}` : null;
	useEffect(() => {
		if (!askPct || !pctKey) return;
		let live = true;
		deps.percentile(askPct.number, level, askPct.elapsedMs).then(
			(value) => live && setPct({ key: pctKey, value }),
			() => undefined,
		);
		return () => {
			live = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pctKey]);
	const pctText = askPct && pct?.key === pctKey ? percentileLine(level, askPct.elapsedMs, pct.value) : null;

	// Your challenges: the latest few, asked once.
	const mine = useMemo(() => myChallenges(), []);
	// And the ones you took, recorded on this device as you finished them.
	const taken = useMemo(() => tallyTaken(challengesTaken()), []);
	const asked = Math.min(mine.length, CHALLENGES_ASKED);
	const [tally, setTally] = useState<ChallengeTally | null>(null);
	const [tallyLoading, setTallyLoading] = useState(mine.length > 0);
	useEffect(() => {
		if (mine.length === 0) return;
		let live = true;
		void Promise.all(mine.slice(-CHALLENGES_ASKED).map((c) => deps.challengeResults(c.code).catch(() => null))).then((results) => {
			if (!live) return;
			const t = tallyChallenges(results);
			setTally(t.counted > 0 ? t : null);
			setTallyLoading(false);
		});
		return () => {
			live = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const used = (control: 'level' | 'month' | 'times') => track('stats_control_used', { control });

	const hasDailies = summary.dailies.completed > 0;
	const perfect = LEVELS.reduce((n, l) => n + records[l].perfect, 0);
	const anyLate = useMemo(() => solveHistory(stats).some((x) => !x.onTime), [stats]);
	const hasChallenges = mine.length > 0 || taken.taken > 0;
	// Any challenge on this device means something to show, even if the stats were lost.
	const empty = summary.totalCompleted === 0 && streak.current === 0 && streak.best === 0 && first === null && !hasChallenges;
	// C · the always-there line, at the foot (empty or not): hidden only while Sparrow is detected as installed, or with no way to install here.
	const install = useInstallFlow('stats_row');
	const showRow = lineVisible({ installed: install.installed, hasRoute: install.route !== null });
	const words = lineCopy(install.route, install.platform.desktop);

	// Tapping Nib 12 times turns on dev mode (dev/mode.ts).
	const nibTaps = useNibTaps();

	const nibPose = streak.todayCounted ? (streak.isBest ? 'cheeky' : 'happy') : streak.current > 0 ? 'lookup' : 'idle';
	const streakText = streakLine(streak);

	return (
		<div className="page stats">
			{/* The brand row is the page's banner, outside <main> (QA4-13); both scroll together. */}
			<div className="page-body page-scroll">
				<BrandRow streak={streak.current} after={<AppMenu screen="stats" install={{ todayKey }} />} />
				<main className="page-main">
					<h1 className="page-title">You</h1>
					{empty ? (
						<div className="empty" data-testid="stats-empty">
							<Nib pose="idle" className="empty-nib" onClick={nibTaps.tap} />
							<p className="empty-line">No stats yet.</p>
							<p className="empty-sub">Solve today’s Sparrow and your streak and times will start here.</p>
							<Link to="/" how="up" className="btn lg primary fit">
								Play today’s Sparrow
								<Wedge direction="right" />
							</Link>
							{showRow && <InstallRow label={words.label} sub={words.sub} onAdd={install.start} />}
						</div>
					) : (
						<div className="st-body">
							<div className="st-col">
								{/* The streak, then the days you've played. */}
								<section className="st-hero" aria-labelledby="st-streak">
									<h2 className="sr-only" id="st-streak">
										Streak
									</h2>
									<div className="st-hero-row">
										<p className="st-streak">
											<span className="st-streak-n">
												<Flame className="flame" />
												{streak.current}
											</span>
											<span className="st-streak-lb">
												{plural(streak.current, 'day')} in a row{streak.todayCounted ? ' · today’s in' : ''}
											</span>
										</p>
										{streak.best > streak.current && streak.best > 1 && (
											<p className="st-best">
												{/* Read as one phrase, label first: "Best streak, 14 days" (J7). */}
												<span className="st-best-n" aria-hidden="true">
													{streak.best}
												</span>
												<span className="st-best-lb" aria-hidden="true">
													Best streak
												</span>
												<span className="sr-only">{`Best streak, ${streak.best} ${plural(streak.best, 'day')}`}</span>
											</p>
										)}
										<Nib pose={nibPose} className="st-nib" onClick={nibTaps.tap} />
									</div>
									{(streakText || !streak.todayCounted) && (
										<p className="st-streak-line">
											{streakText}
											{!streak.todayCounted && (
												<>
													{streakText && ' '}
													<Link to="/" how="up" className="tlink st-link">
														Play today’s Sparrow
													</Link>
												</>
											)}
										</p>
									)}
									{/* Under the line: protected by the streak saver, or a way to turn it on (push/StreakGuard.tsx). */}
									<StreakGuard streak={streak.current} />
								</section>

								{first !== null && (
									<Section id="st-days" title="Days played">
										<MonthCalendar
											view={month}
											thisYear={thisYear}
											since={first}
											canBack={ymIndex(ym) > ymIndex(firstYm)}
											canForward={ymIndex(ym) < ymIndex(now)}
											onStep={(n) => {
												used('month');
												setYm((cur) => shiftMonth(cur, n));
											}}
										/>
										<CalendarKey late={anyLate} />
									</Section>
								)}

							</div>

							<div className="st-col">
								{hasDailies && (
									<Section id="st-bests" title="Personal bests" aside={<span className="st-aside">Quickest, no reveals</span>}>
										<LevelCards
											records={records}
											level={level}
											onPick={(l) => {
												if (l !== level) used('level');
												setLevel(l);
											}}
										>
											{solves.length >= 2 ? (
												<TimesChart key={level} level={level} solves={solves} record={record} />
											) : solves.length === 1 ? (
												<p className="st-note">
													One {LEVEL_NAME[level]} so far: {formatDuration(solves[0]!.elapsedMs)}. The chart starts from your second.
												</p>
											) : (
												<p className="st-note">No {LEVEL_NAME[level]} solved yet. Pick it on Today.</p>
											)}
											{pctText && <p className="st-pct">{pctText}</p>}
											{solves.length >= 2 && <EveryTime level={level} solves={solves} thisYear={thisYear} onOpen={() => used('times')} />}
										</LevelCards>
									</Section>
								)}

								{hasDailies && flow.best >= 2 && (
									<Section id="st-flow" title="Flow">
										<FlowBody flow={flow} thisYear={thisYear} />
									</Section>
								)}

								{hasChallenges && (
									<Section id="st-ch" title="Challenges">
										<ChallengesBody sent={mine.length} tally={tally} loading={tallyLoading} asked={asked} taken={taken} />
									</Section>
								)}
							</div>

							<footer className="st-foot">
								<p className="st-all">
									<b>
										{summary.totalCompleted} {plural(summary.totalCompleted, 'Sparrow')} solved
									</b>
									{first ? ` since ${longDate(first, thisYear)}` : ''}.{' '}
									{andList([
										hasDailies ? `${summary.dailies.completed} ${plural(summary.dailies.completed, 'daily puzzle')} on ${summary.daysCompleted} ${plural(summary.daysCompleted, 'day')}` : null,
										summary.practiceCompleted ? `${summary.practiceCompleted} ${plural(summary.practiceCompleted, 'practice puzzle')}` : null,
									])}
									{summary.dailies.completed >= 3 && ` ${helpLine(perfect, summary.dailies.completed)}`}
								</p>
								<p className="st-device">Kept on this device. No account needed.</p>
								{showRow && <InstallRow label={words.label} sub={words.sub} onAdd={install.start} />}
							</footer>
						</div>
					)}
				</main>
			</div>
			<TabBar current="stats" />
			{install.sheet}
			{/* Nib's secret (dev/mode.ts): the shared toast, its words read once, politely. */}
			<div className="sr-only" role="status" aria-live="polite">
				{nibTaps.note ?? ''}
			</div>
			{nibTaps.note && <Toast key={nibTaps.serial} text={nibTaps.note} place="above-tabbar" testId="dev-tap-note" />}
		</div>
	);
}
